import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Select from "react-select";
import { Link } from "react-router-dom";
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import {faPlus} from '@fortawesome/fontawesome-free-solid/';
import update from 'immutability-helper';
import { connect } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';
import i18next from 'i18next';

import { ChecklistConfigsService, GroupingsService, TagsService } from '../../services';
import ChecklistSchedule from './checklist_schedule';
import ChecklistTask from './checklist_task';
import ChecklistTaskGroup from './checklist_task_group';
import { TextInput } from '../TextInput';
import { PageLevelAccess } from '../PageLevelAccess';

import config from "config";
import { validateChecklistTimerConfig } from './checklist_error_helper';
import { CheckboxInput } from '../CheckboxInput';
import ChecklistCorrectiveAction from './checklist_corrective_action';

const ChecklistConfigExpandableHeader = ({expanded}) => (
    expanded ? 
        <span><img src={`${config.WEB_BASE_URL}/images/icon/collapse.svg`}/> {i18next.t('form-labels.collapse-all')}</span> 
        : 
        <span><img src={`${config.WEB_BASE_URL}/images/icon/expand.svg`}/>{i18next.t('form-labels.expand-all')}</span>
);

const ChecklistConfigHeader = ({readOnly, expanded, toggleExpanded, addTaskGroupToChecklist, addTaskToChecklist}) => (
    readOnly ?
        (<ul className="list-inline clearfix remove-margin-bottom">
            <li className="pull-left"><h3 className="remove-margin-bottom">{i18next.t('pages.checklists.task-list')}</h3></li>
            <li className="pull-right">
                <button className="btn btn-default btn-sm" type="button" onClick={toggleExpanded}>
                    <ChecklistConfigExpandableHeader expanded={expanded} />
                </button>
            </li>
        </ul>)
        :             
        (<ul className="list-inline clearfix remove-margin-bottom">
            <li className="pull-left"><h3 className="remove-margin-bottom">{i18next.t('pages.checklists.task-list')}</h3></li>
            <li className="pull-right">
                <button className="btn btn-default btn-sm" type="button" onClick={addTaskGroupToChecklist}>
                    <FontAwesomeIcon icon={faPlus}/> {i18next.t('pages.checklists.task-group')}
                </button>
            </li>
            <li className="pull-right">
                <button className="btn btn-default btn-sm" type="button" onClick={addTaskToChecklist}>
                    <FontAwesomeIcon icon={faPlus}/> {i18next.t('pages.checklists.task')}
                </button>
            </li>
            <li className="pull-right">
                <button className="btn btn-default btn-sm" type="button" onClick={toggleExpanded}>
                    <ChecklistConfigExpandableHeader expanded={expanded} />
                </button>
            </li>
        </ul>)
);

const ChecklistConfigTaskList = ({expanded, readOnly, taskList, areaTags, stepTypes, checklistDeviceTags, onAddTaskToGroup, onTaskGroupUpdate, onTaskGroupRemove, onTaskUpdate, onTaskDelete, onTaskCopy, onTaskMoveUp, onTaskMoveDown}) => {
        if (taskList) {
            return (
                <div className="checklist-padding">
                    {
                        taskList.map((entry) => {
                            if (entry.group) {
                                return (
                                    <ChecklistTaskGroup 
                                        key={`gid-${entry.group.uid}`} 
                                        tags={areaTags}
                                        taskGroup={entry.group} 
                                        expanded={expanded}
                                        readOnly={readOnly}
                                        onAddTaskToGroup={onAddTaskToGroup}
                                        onTaskGroupUpdate={onTaskGroupUpdate}
                                        onTaskGroupRemove={onTaskGroupRemove}
                                    >
                                        {
                                            entry.tasks.map((task) => (
                                                <ChecklistTask 
                                                    key={`gid-${entry.group.uid}-tid-${task.uid}`}
                                                    task={task} 
                                                    stepTypes={stepTypes}
                                                    checklistDeviceTags={checklistDeviceTags}
                                                    expanded={expanded}
                                                    readOnly={readOnly}
                                                    onTaskUpdate={onTaskUpdate}
                                                    onTaskDelete={onTaskDelete}
                                                    onTaskCopy={onTaskCopy}
                                                    onTaskMoveUp={task.isFirst ? null : () => onTaskMoveUp(task)}
                                                    onTaskMoveDown={task.isLast ? null : () => onTaskMoveDown(task)}
                                                />
                                            ))
                                        }
                                    </ChecklistTaskGroup>
                                );
                            } else if (entry.task) {
                                return (
                                    <ChecklistTask
                                        key={`tid-${entry.task.uid}`}
                                        task={entry.task}
                                        stepTypes={stepTypes}
                                        checklistDeviceTags={checklistDeviceTags}
                                        expanded={expanded}
                                        readOnly={readOnly}
                                        onTaskUpdate={onTaskUpdate}
                                        onTaskDelete={onTaskDelete}
                                        onTaskCopy={onTaskCopy}
                                        onTaskMoveUp={entry.task.isFirst ? null : () => onTaskMoveUp(entry.task)}
                                        onTaskMoveDown={entry.task.isLast ? null : () => onTaskMoveDown(entry.task)}
                                    />
                                );
                            }
                        })
                    }
                </div>
            )
        } else {
            return null;
        }
};

const ChecklistFailureCorrectiveActionArea = ({ readOnly, failureCorrectiveAction, stepTypes, checklistDeviceTags, onUpdate }) => {
    const [showModal, setShowModal] = useState(false);
    const correctiveAction = useMemo(() => {
        if (failureCorrectiveAction) {
            let stepsSorted = [...failureCorrectiveAction.checklistTaskOptionConfigs];
            stepsSorted.sort((a, b) => { return a.optionOrder - b.optionOrder; });

            return {
                ...failureCorrectiveAction,
                checklistTaskOptionConfigs: stepsSorted.map((step) => ({ ...step, uid: Math.floor(Math.random() * 99999 + 999000000) })),
            };
        } else {
            return { name: 'Checklist Failure Corrective Action', correctiveAction: true, checklistTaskOptionConfigs: [] };
        }
    }, [failureCorrectiveAction]);
    const [correctiveActionContext, setCorrectiveActionContext] = useState({ correctiveAction: correctiveAction });

    const onSave = useCallback(() => {
        onUpdate(correctiveActionContext.correctiveAction);
        setShowModal(false);
    }, [onUpdate, correctiveActionContext]);

    return (
        <div>
            { ((readOnly && failureCorrectiveAction) || (!readOnly)) &&
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                {`${ readOnly ? 'View' : (failureCorrectiveAction ? 'Edit' : 'Add')} Failure Corrective Action`}
            </button>
            }

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Body style={{backgroundColor: "#f5a623"}}>
                    <div style={{paddingRight: 20}}>
                        <ChecklistCorrectiveAction
                            readOnly={readOnly}
                            inModal={true}
                            stepTypes={stepTypes}
                            checklistDeviceTags={checklistDeviceTags}
                            correctiveActionContext={correctiveActionContext}
                            updateCorrectiveAction={setCorrectiveActionContext}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer style={{backgroundColor: "#f5a623"}}>
                    <button className="btn btn-primary" onClick={onSave}>Save</button>
                    <Button onClick={() => setShowModal(false)}>{i18next.t("form-labels.cancel")}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const ChecklistConfig = ({ me, originalChecklistConfiguration, readOnly, backLinkWithParams, onSubmit, onArchive, onError, onWarning }) => {
    const [groupingOptions, setGroupingOptions] = useState([]);
    const [stepTypes, setStepTypes] = useState([]);
    const [areaTags, setAreaTags] = useState([]);
    const [checklistDeviceTags, setChecklistDeviceTags] = useState([]);
    const [checklistTypeTags, setChecklistTypeTags] = useState([]);
    const [checklistConfiguration, setChecklistConfiguration] = useState(null);
    const [taskGroups, setTaskGroups] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const [loading, setLoading] = useState(true);
    const [showDeleteChecklistModal, setShowDeleteChecklistModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [taskGroupsToSave, setTaskGroupsToSave] = useState([]);
    const [changeComment, setChangeComment] = useState('');

    const checklistIDorUIDEquals = (itemA, itemB) => {
        if (itemA.id == null || itemB.id == null) {
            if (itemA.uid == null || itemB.uid == null) {
                console.error('Unable to match ID or UID');
                console.error(itemA);
                console.error(itemB);
                return false;
            } else {
                return itemA.uid == itemB.uid;
            }
        } else {
            return itemA.id == itemB.id;
        }
    };

    const viewableTaskList = useMemo(() => {
        let updatedTaskList = [];

        if (checklistConfiguration?.checklistTaskConfigs) {
            let checklistTaskConfigs = checklistConfiguration.checklistTaskConfigs
                .sort((a, b) => a.taskOrder - b.taskOrder)
                .map((originalTask) => {
                    let updatedTask = { ...originalTask };
                    updatedTask.isFirst = updatedTask.taskOrder === 0;
                    updatedTask.isLast = updatedTask.taskOrder === checklistConfiguration.checklistTaskConfigs.length - 1;   
                    if (updatedTask.checklistTaskGroup) {
                        const otherTasksInGroup = checklistConfiguration.checklistTaskConfigs
                                                        .filter((task) => !checklistIDorUIDEquals(task, updatedTask))
                                                        .filter((task) => task.checklistTaskGroup && checklistIDorUIDEquals(task.checklistTaskGroup, updatedTask.checklistTaskGroup));
                        updatedTask.isFirstInGroup = updatedTask.isFirst || otherTasksInGroup.length == 0 || updatedTask.taskOrder < otherTasksInGroup[0].taskOrder;
                        updatedTask.isLastInGroup = updatedTask.isLast || otherTasksInGroup.length == 0 || updatedTask.taskOrder > otherTasksInGroup[otherTasksInGroup.length - 1].taskOrder;
                    }
                    return updatedTask;
                });

            checklistTaskConfigs.filter((task) => !task.checklistTaskGroup).forEach((task) => updatedTaskList.push({ task: task }));

            taskGroups.forEach((group) => {
                updatedTaskList.push({
                    group: group,
                    tasks: checklistTaskConfigs.filter((task) => task.checklistTaskGroup && checklistIDorUIDEquals(task.checklistTaskGroup, group)),
                });
            });

            updatedTaskList.sort((a, b) => {
                const aTaskOrder = a.group ? (a.tasks.length > 0 ? a.tasks[a.tasks.length-1].taskOrder : 0) : a.task.taskOrder;
                const bTaskOrder = b.group ? (b.tasks.length > 0 ? b.tasks[b.tasks.length-1].taskOrder : 0) : b.task.taskOrder;
                return aTaskOrder - bTaskOrder;
            })
        }

        return updatedTaskList;
    }, [checklistConfiguration, taskGroups]);

    useEffect(() => {
        setLoading(true);

        if (originalChecklistConfiguration) {
            let modifiableChecklistConfiguration = JSON.parse(JSON.stringify(originalChecklistConfiguration));

            // adding unique IDs for allowing updating additions before saving
            if (modifiableChecklistConfiguration.checklistScheduleConfig && modifiableChecklistConfiguration.checklistScheduleConfig.checklistScheduleTimeConfigs) {
                modifiableChecklistConfiguration.checklistScheduleConfig.checklistScheduleTimeConfigs.forEach((time, index) => {
                    time.uid = index;
                });
            }

            if (modifiableChecklistConfiguration.checklistTaskConfigs) {
                let updatedTaskGroups = [];

                modifiableChecklistConfiguration.checklistTaskConfigs.forEach((task) => {
                    if (task.checklistTaskGroup) {
                        const foundGroup = updatedTaskGroups.find((group) => checklistIDorUIDEquals(group, task.checklistTaskGroup));
                        if (foundGroup) {
                            task.checklistTaskGroup = foundGroup;
                        } else {
                            task.checklistTaskGroup.uid = Math.floor(Math.random() * 99999 + 999000000);
                            updatedTaskGroups.push(task.checklistTaskGroup);
                        }
                    }
                });

                setTaskGroups(updatedTaskGroups);

                modifiableChecklistConfiguration.checklistTaskConfigs.forEach((task, _) => {
                    task.uid = Math.floor(Math.random() * 99999 + 999000000);

                    task.checklistTaskOptionConfigs
                        .sort((a, b) => {
                            return a.optionOrder - b.optionOrder;
                        })
                        .forEach((step, _) => {
                            step.uid = Math.floor(Math.random() * 99999 + 999000000);

                            if (step.type === "selection" && step.checklistTaskSelectionOptions) {
                                step.checklistTaskSelectionOptions
                                    .sort((a, b) => {
                                        return a.selectionOrder - b.selectionOrder;
                                    })
                                    .forEach((selection, index) => {
                                        selection.uid = index;
                                    });
                            }
                        });
                });
            }

            setChecklistConfiguration(modifiableChecklistConfiguration);
        } else {
            setChecklistConfiguration({ name: "", enterprise: { id: me.enterprise.id }, checklistTaskConfigs: [], requiresTaskOrder: false, allowIncompleteTasks: true });
        }
    }, [originalChecklistConfiguration]);

    useEffect(() => {
        setLoading(true);

        let requests = [];

        requests.push(
            GroupingsService.listAll()
                .then((response) => {
                    setGroupingOptions(response.groupings.map((grouping) => ({ label: grouping.name, value: grouping.id, grouping: grouping })));
                })
                .catch((error) => {
                    console.error(error);
                    onError(i18next.t("messages.could-not-load-groupings"));
                })
        );

        requests.push(
            ChecklistConfigsService.getTaskOptionTypes()
                .then((response) => {
                    setStepTypes(response.taskOptionTypes);
                })
                .catch((err) => {
                    console.error(err);
                    onError(i18next.t("messages.could-not-load-step-types"));
                })
        );

        requests.push(
            TagsService.listAll()
                .then((response) => {
                    setAreaTags(response.tags.filter((tag) => tag.tagType && tag.tagType.token === 'area'));
                    setChecklistDeviceTags(response.tags.filter((tag) => tag.tagType && tag.tagType.token === 'checklist-passive-device'));
                    setChecklistTypeTags(response.tags.filter((tag) => tag.tagType && tag.tagType.token === 'checklist-type'));
                })
                .catch((error) => {
                    console.error(error);
                    onError(i18next.t("messages.could-not-load-area-tags"));
                })
        );

        Promise.all(requests).then(() => setLoading(false));
    }, []);

    const updateChecklistName = useCallback((name) => {
        setChecklistConfiguration(update(checklistConfiguration, { name: { $set: name } }));
        setErrors({});
    }, [checklistConfiguration]);

    const toggleTaskOrderEnforcement = useCallback(() => {
        if (checklistConfiguration?.requiresTaskOrder === true) {
            setChecklistConfiguration(update(checklistConfiguration, { requiresTaskOrder: { $set: false } }));
        } else {
            setChecklistConfiguration(update(checklistConfiguration, { requiresTaskOrder: { $set: true } }));
        }
        setErrors({});
    }, [checklistConfiguration]);

    const toggleAllowIncompleteTasks = useCallback(() => {
        if (checklistConfiguration?.allowIncompleteTasks === true) {
            setChecklistConfiguration(update(checklistConfiguration, { allowIncompleteTasks: { $set: false } }));
        } else {
            setChecklistConfiguration(update(checklistConfiguration, { allowIncompleteTasks: { $set: true } }));
        }
        setErrors({});
    }, [checklistConfiguration]);

    const updateGroupsForChecklist = useCallback((selected) => {
        setChecklistConfiguration(
            update(checklistConfiguration, {
                deviceGroupings: { $set: selected.map((option) => option.grouping) },
            })
        );
    }, [checklistConfiguration]);

    const updateTagsForChecklist = useCallback((selected) => {
        setChecklistConfiguration(
            update(checklistConfiguration, {
                tags: { $set: selected.map((option) => option.tag) },
            })
        );
    }, [checklistConfiguration]);

    const failureCorrectiveActionChanged = useCallback((failureCorrectiveAction) => {
        if (!failureCorrectiveAction.checklistTaskOptionConfigs || failureCorrectiveAction.checklistTaskOptionConfigs.count == 0) {
            setChecklistConfiguration(
                update(checklistConfiguration, {
                    failureCorrectiveAction: { $set: null }
                })
            );
        } else {
            setChecklistConfiguration(
                update(checklistConfiguration, {
                    failureCorrectiveAction: { $set: failureCorrectiveAction }
                })
            );
        }

    }, [checklistConfiguration]);

    const handleScheduleUpdate = useCallback((schedule) => {
        setChecklistConfiguration(update(checklistConfiguration, { checklistScheduleConfig: { $set: schedule } }));
    }, [checklistConfiguration]);

    const addTaskToChecklist = useCallback((group) => {
        let tasks = [...checklistConfiguration.checklistTaskConfigs];
        const countInGroup = group ? tasks.filter((t) => t.checklistTaskGroup && checklistIDorUIDEquals(t.checklistTaskGroup, group)).length : -1;

        // Adding to a group, existing tasks exist and not the first task in a group
        if (group && tasks.length > 0 && countInGroup > 0) {
            const groupNextTaskOrder = Math.max(...tasks.map((t) => (t.checklistTaskGroup?.uid == group.uid ? t.taskOrder : -1))) + 1;

            tasks = tasks.map((task) => task.taskOrder >= groupNextTaskOrder ? ({...task, taskOrder: task.taskOrder + 1}) : task);
            tasks.push({
                uid: Math.floor(Math.random() * 99999 + 999000000),
                name: "",
                checklistTaskGroup: group,
                checklistTaskOptionConfigs: [{ uid: 0 }],
                taskOrder: groupNextTaskOrder,
            });
        } else {
            tasks.push({
                uid: Math.floor(Math.random() * 99999 + 999000000),
                name: "",
                checklistTaskGroup: group,
                checklistTaskOptionConfigs: [{ uid: 0 }],
                taskOrder: tasks.length == 0 ? 0 : Math.max(...tasks.map((t) => t.taskOrder)) + 1,
            });
        }

        tasks.sort((a, b) => { return a.taskOrder - b.taskOrder; })

        setChecklistConfiguration(update(checklistConfiguration, { checklistTaskConfigs: { $set: tasks } }));
    }, [checklistConfiguration]);

    const addTaskGroupToChecklist = useCallback(() => {
        const groupToAdd = {
            uid: Math.floor(Math.random() * 99999 + 999000000),
            name: "",
            description: "",
            enterprise: { id: checklistConfiguration.enterprise.id },
        };

        setTaskGroups([...taskGroups, groupToAdd]);
        setTaskGroupsToSave([...taskGroupsToSave, groupToAdd]);

        addTaskToChecklist(groupToAdd);
    }, [checklistConfiguration, taskGroups, taskGroupsToSave]);

    const handleTaskGroupUpdate = useCallback((taskGroupToUpdate) => {
        if (taskGroupsToSave.find((group) => group.uid === taskGroupToUpdate.uid)) {
            setTaskGroupsToSave(taskGroupsToSave.map((group) => (group.uid === taskGroupToUpdate.uid ? { ...taskGroupToUpdate, errors: {} } : group)));
        } else {
            setTaskGroupsToSave([...taskGroupsToSave, { ...taskGroupToUpdate, errors: {} }]);
        }

        setTaskGroups(taskGroups.map((group) => (group.uid === taskGroupToUpdate.uid ? { ...taskGroupToUpdate, errors: {} } : group)));
    }, [checklistConfiguration]);

    const handleTaskUpdate = useCallback((taskToUpdate) => {
        setChecklistConfiguration(
            update(checklistConfiguration, {
                checklistTaskConfigs: { $set: checklistConfiguration.checklistTaskConfigs.map((task) => (task.uid === taskToUpdate.uid ? { ...taskToUpdate, errors: {} } : task)) },
            })
        );
    }, [checklistConfiguration]);

    const handleTaskCopy = useCallback((taskToCopy) => {
        let tasks = [...checklistConfiguration.checklistTaskConfigs];
        let copiedTask = JSON.parse(JSON.stringify(taskToCopy));

        copiedTask.id = null; 
        copiedTask.uid = Math.floor(Math.random() * 99999 + 999000000);
        copiedTask.name = `${taskToCopy.name} ${i18next.t("copy")}`;

        tasks.push(copiedTask);

        setChecklistConfiguration(update(checklistConfiguration, { checklistTaskConfigs: { $set: tasks } }));
    }, [checklistConfiguration]);

    const handleTaskRemove = useCallback((taskToRemove) => {
        const index = checklistConfiguration.checklistTaskConfigs.findIndex((tc) => tc.uid === taskToRemove.uid);
        if (index == -1) {
            console.error(`Unable to find index for ${taskToRemove.uid}`);
        } else {
            let updatedChecklistTaskConfigs = [...checklistConfiguration.checklistTaskConfigs];
            updatedChecklistTaskConfigs.splice(index, 1);
            setChecklistConfiguration(update(checklistConfiguration, { checklistTaskConfigs: { $set: updatedChecklistTaskConfigs } }));
        }
    }, [checklistConfiguration]);

    const handleTaskMove = useCallback((originalTask, direction) => {

        // If this is the first or last entry in a group then it may be moving out of that group
        if (originalTask.checklistTaskGroup && ((originalTask.isFirstInGroup && direction === "up") || (originalTask.isLastInGroup && direction === "down"))) {

            const updatedChecklistTaskConfigs = checklistConfiguration.checklistTaskConfigs
                                .map((task) => checklistIDorUIDEquals(task, originalTask) ? { ...originalTask, checklistTaskGroup: null } : task);

            setChecklistConfiguration(update(checklistConfiguration, { checklistTaskConfigs: {  $set: updatedChecklistTaskConfigs } }));

            // Remove any empty groups
            setTaskGroups(taskGroups.filter((group) => 
                updatedChecklistTaskConfigs.some((task) => task.checklistTaskGroup && task.checklistTaskGroup.uid === group.uid))
            );

            setTaskGroupsToSave(taskGroupsToSave.filter((group) =>
                updatedChecklistTaskConfigs.some((task) => task.checklistTaskGroup && task.checklistTaskGroup.uid === group.uid))
            );

        } else {

            let newTaskOrder = -1;

            if (direction === "up" && originalTask.taskOrder !== 0) {
                newTaskOrder = originalTask.taskOrder - 1;
            } else if (direction === "down" && originalTask.taskOrder !== checklistConfiguration.checklistTaskConfigs.length - 1) {
                newTaskOrder = originalTask.taskOrder + 1;
            }

            if (newTaskOrder >= 0) {
                let updatedTask = { ...originalTask };

                let taskToSwap = checklistConfiguration.checklistTaskConfigs.find((task) => task.taskOrder === newTaskOrder);

                // If the task to swap with is in a group then the update simply moves the input task to that group
                if ((taskToSwap.checklistTaskGroup && !updatedTask.checklistTaskGroup) ||
                    (taskToSwap.checklistTaskGroup && updatedTask.checklistTaskGroup && taskToSwap.checklistTaskGroup.uid !== updatedTask.checklistTaskGroup.uid)) {
                    updatedTask.checklistTaskGroup = taskToSwap.checklistTaskGroup;
                } else {
                    taskToSwap = {...taskToSwap, taskOrder: originalTask.taskOrder};
                    updatedTask.taskOrder = newTaskOrder;
                }

                const updatedTaskList = checklistConfiguration.checklistTaskConfigs.map((existingTask) => {
                    if (existingTask.uid === updatedTask.uid) {
                        return updatedTask;
                    } else if (existingTask.uid === taskToSwap.uid) {
                        return taskToSwap;
                    } else {
                        return existingTask;
                    }
                });

                setChecklistConfiguration(update(checklistConfiguration, { checklistTaskConfigs: { $set: updatedTaskList } }));
            } else {
                console.error('Error updating task order.')
            }

        }

    }, [checklistConfiguration, onError]);

    const handleTaskGroupRemove = useCallback((groupToRemove) => {
        const tasksInGroup = checklistConfiguration.checklistTaskConfigs.filter((task) => task.checklistTaskGroup && task.checklistTaskGroup.uid === groupToRemove.uid);
        if (tasksInGroup && tasksInGroup.length > 0) {
            onWarning(i18next.t("messages.cannot-remove-group-with-tasks"));
        } else {
            setTaskGroups(taskGroups.filter((tg) => tg.uid != groupToRemove.uid));
            setTaskGroupsToSave(taskGroupsToSave.filter((tg) => tg.uid != groupToRemove.uid));
        }
    }, [checklistConfiguration, onWarning]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();

        let errors = {};
        let containsErrors = false;
        let updatedTasks = [];
        let contentToUpload = [];

        if (checklistConfiguration.name === "") {
            containsErrors = true;
            errors.name = i18next.t("messages.please-enter-checklist-name");
        }

        if (checklistConfiguration.checklistTaskConfigs) {
            updatedTasks = checklistConfiguration.checklistTaskConfigs.map((originalTask) => {
                let task = { ...originalTask };
                task.errors = {};
                if (task.name === "") {
                    containsErrors = true;
                    task.errors.name = i18next.t("messages.please-enter-task-name");
                }
                
                if (task.checklistTaskTimerConfig && task.checklistTaskTimerConfig.default === false) {
                    const {hasErrors, updatedTimer} = validateChecklistTimerConfig(task.checklistTaskTimerConfig);
                    if (hasErrors) {
                        task.checklistTaskTimerConfig = updatedTimer;
                        containsErrors = true;
                    }
                }

                if (task.checklistTaskOptionConfigs) {
                    task.checklistTaskOptionConfigs = task.checklistTaskOptionConfigs.map((originalStep) => {
                        let step = { ...originalStep };
                        step.errors = {};

                        if (!step.type) {
                            containsErrors = true;
                            step.errors.type = i18next.t("messages.please-select-step-type");
                        }

                        if (!step.name || step.name === "") {
                            containsErrors = true;
                            step.errors.name = i18next.t("messages.please-select-step-name");
                        }

                        if (step.checklistTaskOptionType && step.type === "selection") {
                            if (step.checklistTaskSelectionOptions) {
                                step.checklistTaskSelectionOptions = step.checklistTaskSelectionOptions.map((originalSelectOption) => {
                                    let selectOption = { ...originalSelectOption };
                                    selectOption.errors = {};
                                    if (!selectOption.name) {
                                        containsErrors = true;
                                        selectOption.errors.name = i18next.t("messages.please-enter-selection-name");
                                    }
                                    return selectOption;
                                });
                            }
                        } else if (step.checklistTaskOptionType && step.type === "temperature") {
                            if (step.passiveOnly === true && !step.checklistTag) {
                                containsErrors = true;
                                step.errors.checklistTag = "Tag required when passive only enabled."
                            }

                            // Temperature must be
                            // UNDER => Under the low value, violation is anything over the low value
                            // OVER => Over the high value, violation is anything under the high value
                            // BETWEEN => Over the low value and under the high value, violation is anything under the low value or over the high value
                            if ((step.constraintType === "UNDER" || step.constraintType === "BETWEEN") && !step.constraintLowValue) {
                                containsErrors = true;
                                step.errors.constraintLowValue = "Low value required for constraint type."
                            } 
                            if ((step.constraintType === "OVER" || step.constraintType === "BETWEEN") && !step.constraintHighValue) {
                                containsErrors = true;
                                step.errors.constraintHighValue = "High value required for constraint type.";
                            }

                            if (step.constraintLowValue && isNaN(parseFloat(step.constraintLowValue))) {
                                containsErrors = true;
                                step.errors.constraintLowValue = "Low value must be a number.";
                            }
                            if (step.constraintHighValue && isNaN(parseFloat(step.constraintHighValue))) {
                                containsErrors = true;
                                step.errors.constraintHighValue = "High value must be a number.";
                            }
                        } else if (step.checklistTaskOptionType && step.type === "content") {
                            if (!step.contentType) {
                                containsErrors = true;
                                step.errors.contentType = "Content type required.";
                            }

                            if (!step.contentFile && step.uploadNeeded === true) {
                                containsErrors = true;
                                step.errors.contentFile = "Content required.";
                            }

                            if (!containsErrors && step.uploadNeeded === true) {
                                contentToUpload.push({fileToUpload: step.contentFile, token: step.contentToken});
                            }
                        }

                        return step;
                    });
                }

                return task;
            });
        }

        const updatedTaskGroups = taskGroups.map((originalGroup) => {
            let group = { ...originalGroup };
            group.errors = {};
            if (group.name === "") {
                containsErrors = true;
                group.errors.name = i18next.t("messages.please-enter-task-group-name");
            }
            return group;
        });

        if (containsErrors) {
            console.error(`Checklist config contains errors`)
            console.error(errors);

            setExpanded(true);
            setErrors(errors);
            setTaskGroups(updatedTaskGroups);
            setChecklistConfiguration(update(checklistConfiguration, { checklistTaskConfigs: { $set: updatedTasks } }));
        } else {
            let requests = [];

            taskGroupsToSave.forEach((group) => {
                if (group.id) {
                    requests.push(
                        ChecklistConfigsService.saveTaskGroup(group.id, group)
                            .then((_) => {
                                return group;
                            })
                            .catch((error) => {
                                console.error(error);
                                onError(i18next.t("messages.error-saving-task-group"));
                            })
                    );
                } else {
                    requests.push(
                        ChecklistConfigsService.addTaskGroup(group)
                            .then((response) => {
                                return { id: response.id, uid: group.uid };
                            })
                            .catch((error) => {
                                console.error(error);
                                onError(i18next.t("messages.error-creating-task-group"));
                            })
                    );
                }
            });

            contentToUpload.forEach(({fileToUpload, token}) => requests.push(ChecklistConfigsService.uploadContent(fileToUpload, token)));

            Promise.all(requests).then((responses) => {
                const savedGroups = responses.filter((response) => response);
                const allGroups = savedGroups.concat(taskGroups.filter((tg) => !savedGroups.find((sg) => sg.uid === tg.uid)));
                onSubmit({
                    ...checklistConfiguration,
                    checklistConfigVersionComment: {comment: changeComment},
                    checklistTaskConfigs: checklistConfiguration.checklistTaskConfigs.map((taskConfig) =>
                        taskConfig.checklistTaskGroup ? { ...taskConfig, checklistTaskGroup: allGroups.find((g) => g.uid === taskConfig.checklistTaskGroup.uid) } : taskConfig
                    ),
                });
            });
        }
    }, [checklistConfiguration, taskGroupsToSave, taskGroups, changeComment, onSubmit, onError]);

    return loading ? (
        <div className="loader">{i18next.t("loading")}</div>
    ) : (
        <div className="checklist-config">
            <div className="row">
                <div className="col-sm-12">
                    <div className="checklist-config-content-box">
                        <div className="row">
                            <div className="col-sm-6">
                                <TextInput
                                    input={{
                                        value: checklistConfiguration.name,
                                        onChange: (event) => updateChecklistName(event.target.value),
                                    }}
                                    type="text"
                                    label={i18next.t("form-labels.name")}
                                    readOnly={readOnly}
                                    meta={errors.name ? { touched: true, error: errors.name } : {}}
                                />
                            </div>
                            <div className="col-sm-6">
                                <label>{i18next.t("form-labels.groups")}</label>
                                <Select
                                    options={groupingOptions}
                                    value={checklistConfiguration?.deviceGroupings?.map((grouping) => ({ label: grouping.name, value: grouping.id, grouping: grouping }))}
                                    onChange={updateGroupsForChecklist}
                                    isClearable={true}
                                    isSearchable={true}
                                    isMulti
                                    isDisabled={readOnly}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder={i18next.t("select")}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-6">
                                <label>Task Enforcement</label>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <CheckboxInput
                                            input={{ checked: checklistConfiguration?.requiresTaskOrder === true, onChange: toggleTaskOrderEnforcement, disabled: readOnly }}
                                            label="Tasks must be completed in order"
                                            className="inline"
                                            meta={{}}
                                        />
                                    </div>

                                    <div className="col-sm-6">
                                        <CheckboxInput
                                            input={{ checked: checklistConfiguration?.allowIncompleteTasks === true, onChange: toggleAllowIncompleteTasks, disabled: readOnly }}
                                            label="Allow incomplete tasks"
                                            className="inline"
                                            meta={{}}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-6">
                                <label>{i18next.t("form-labels.tags")}</label>
                                <Select
                                    options={checklistTypeTags.map((tag) => ({ label: tag.name, value: tag.id, tag: tag }))}
                                    value={checklistConfiguration?.tags?.map((tag) => ({ label: tag.name, value: tag.id, tag: tag }))}
                                    onChange={updateTagsForChecklist}
                                    isClearable={true}
                                    isSearchable={true}
                                    isMulti
                                    isDisabled={readOnly}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder={i18next.t("select")}
                                />
                            </div>
                        </div>

                        <div className="row" style={{paddingTop: 15}}>
                            <div className="col-sm-6">
                                <label>&nbsp;</label>
                                <ChecklistFailureCorrectiveActionArea
                                    readOnly={readOnly}
                                    failureCorrectiveAction={checklistConfiguration?.failureCorrectiveAction}
                                    stepTypes={stepTypes}
                                    checklistDeviceTags={checklistDeviceTags}
                                    onUpdate={failureCorrectiveActionChanged}
                                />
                            </div>

                            {checklistConfiguration.id ? 
                                <div className="col-sm-6">                                        
                                    <label>Change Comment - Version {checklistConfiguration.version + (readOnly ? 0 : 1)}</label>
                                    <input
                                        value={readOnly ? checklistConfiguration.checklistConfigVersionComment?.comment : changeComment}
                                        onChange={(val) => setChangeComment(val.target.value)}
                                        type="textarea"
                                        name="notes"
                                        maxLength = "1000"
                                        rows="4"
                                        readOnly={readOnly}
                                        style={{width: '100%', height: '65px', paddingBottom: '40px', color: 'light-gray', borderWidth: 1, borderColor: 'hsl(0, 0%, 90%)', backgroundColor: readOnly ? 'hsl(0, 0%, 95%)' : 'white'}}
                                        placeholder={ readOnly ? '' : 'Entery comment text here' }
                                    />
                                </div> 
                                : 
                                <div className="col-sm-6">
                                    <label>&nbsp;</label>
                                    &nbsp;
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
            <br />

            <div className="row">
                <div className="col-sm-12">
                    <h5>{i18next.t("form-labels.schedule")}</h5>
                    <div className="checklist-config-content-box">
                        <ChecklistSchedule schedule={checklistConfiguration.checklistScheduleConfig} readOnly={readOnly} onScheduleUpdate={handleScheduleUpdate} />
                    </div>
                </div>
            </div>
            <br />

            <ChecklistConfigHeader
                readOnly={readOnly}
                expanded={expanded}
                toggleExpanded={() => setExpanded(!expanded)}
                addTaskGroupToChecklist={addTaskGroupToChecklist}
                addTaskToChecklist={() => addTaskToChecklist(null)}
            />

            <ChecklistConfigTaskList
                expanded={expanded}
                readOnly={readOnly}
                taskList={viewableTaskList}
                areaTags={areaTags}
                stepTypes={stepTypes}
                checklistDeviceTags={checklistDeviceTags}
                onAddTaskToGroup={addTaskToChecklist}
                onTaskGroupUpdate={handleTaskGroupUpdate}
                onTaskGroupRemove={handleTaskGroupRemove}
                onTaskUpdate={handleTaskUpdate}
                onTaskDelete={handleTaskRemove}
                onTaskCopy={handleTaskCopy}
                onTaskMoveUp={(task) => handleTaskMove(task, "up")}
                onTaskMoveDown={(task) => handleTaskMove(task, "down")}
            />

            {!readOnly && (
                <div>
                    <hr />
                    <ul className="list-inline clearfix" style={{ padding: "5px" }}>
                        <li>
                            <button className="btn btn-primary" onClick={handleSubmit}>
                                {i18next.t("form-labels.save")}
                            </button>
                        </li>
                        <li>
                            <Link to={backLinkWithParams ? backLinkWithParams.link : `/config/checklists`}>
                                <button className="btn btn-default">{i18next.t("form-labels.cancel")}</button>
                            </Link>
                        </li>
                        {checklistConfiguration.id && (
                            <PageLevelAccess to="/config/checklists/edit/:checklistId#delete">
                                <li>
                                    <button className="btn btn-danger" onClick={() => setShowDeleteChecklistModal(true)}>
                                        {i18next.t("archive")}
                                    </button>
                                </li>
                            </PageLevelAccess>
                        )}
                    </ul>
                </div>
            )}

            <Modal show={showDeleteChecklistModal} onHide={() => setShowDeleteChecklistModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {i18next.t("pages.checklists.archive-checklist")}: {checklistConfiguration.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>{i18next.t("messages.are-you-sure-delete")}</Modal.Body>
                <Modal.Footer>
                    <a className="btn btn-danger" onClick={onArchive}>
                        {i18next.t("archive")}
                    </a>
                    <Button onClick={() => setShowDeleteChecklistModal(false)}>{i18next.t("form-labels.cancel")}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default connect(({user}) => ({ me: user.me }))(ChecklistConfig);